import { ReactElement, ReactNode } from 'react';
import useMedia from 'react-use/esm/useMedia';
import useObservable from 'react-use/esm/useObservable';

import { CssBaseline } from '@material-ui/core';

import {
  AppTheme,
  ExtensionDefinition,
  appThemeApiRef,
  createExtension,
  useApi,
} from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';

import { IconMix, IconMoon } from '@spotify/confidence-core-react';

import './styles/app-styles.css';
import { EncoreThemeProvider } from './themes/encore';

const AppThemeProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const themeApi = useApi(appThemeApiRef);
  const themeId = useObservable(
    themeApi.activeThemeId$(),
    themeApi.getActiveThemeId(),
  );
  const prefersDark = useMedia('(prefers-color-scheme: dark)', false);
  const selectedTheme = themeApi
    .getInstalledThemes()
    .find(theme => theme.id === themeId);
  const mode = selectedTheme?.variant ?? (prefersDark ? 'dark' : 'light');

  return (
    <EncoreThemeProvider mode={mode}>
      <CssBaseline>{children}</CssBaseline>
    </EncoreThemeProvider>
  );
};

export const themeConfigs: AppTheme[] = [
  {
    id: 'encore-dark',
    title: 'Dark',
    variant: 'dark',
    icon: <IconMoon />,
    Provider: AppThemeProvider,
  },
  {
    id: 'encore-light',
    title: 'Light',
    variant: 'light',
    icon: <IconMix />,
    Provider: AppThemeProvider,
  },
];

const extensions: ExtensionDefinition[] = [];

// disabling the default themes from Backstage
for (const id of ['light', 'dark']) {
  if (!themeConfigs.some(theme => theme.id === id)) {
    extensions.push(
      createExtension({
        kind: 'theme',
        name: id,
        attachTo: { id: 'api:app/app-theme', input: 'themes' },
        disabled: true,
        output: [],
        factory: () => [],
      }),
    );
  }
}

extensions.push(
  ...themeConfigs.map(theme =>
    ThemeBlueprint.make({
      name: theme.id,
      params: { theme },
    }),
  ),
);

export const AppThemes = extensions;
